import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  input,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';

import { CountriesPhoneService } from './countries-phone.service';
import { resolveDefaultCountry } from './default-country.utils';
import { CountryPhone } from './phone-input.models';
import {
  cleanPhoneInput,
  parseE164,
  toE164,
  tryParseFullNumber,
} from './phone.utils';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PhoneInputComponent),
    },
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatSelectModule],
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit {
  readonly placeholder = input<string>('');

  readonly countries = signal<CountryPhone[]>([]);
  readonly selectedCountry = signal<CountryPhone | null>(null);
  readonly nationalNumber = signal<string>('');
  readonly isDisabled = signal<boolean>(false);

  private readonly countriesPhoneService = inject(CountriesPhoneService);
  private readonly cdr = inject(ChangeDetectorRef);
  private pendingCountryIso2: string | null = null;
  private pendingE164Value: string | null = null;

  onChange: (value: string | null) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    this.countriesPhoneService.getCountries().subscribe((list) => {
      this.countries.set(list);
      if (this.pendingCountryIso2) {
        const found = list.find((c) => c.iso2 === this.pendingCountryIso2);
        if (found) {
          this.selectedCountry.set(found);
        }
        this.pendingCountryIso2 = null;
      }
      if (!this.selectedCountry() && list.length) {
        this.selectedCountry.set(resolveDefaultCountry(list));
      }
      if (this.pendingE164Value !== null) {
        const state = parseE164(this.pendingE164Value, list);
        this.selectedCountry.set(state.country);
        this.nationalNumber.set(state.nationalNumber);
        this.pendingE164Value = null;
      }
      if (this.selectedCountry() && this.nationalNumber()) {
        this.emitValue();
      }
      this.cdr.markForCheck();
    });
  }

  compareCountry(a: CountryPhone | null, b: CountryPhone | null): boolean {
    if (!a || !b) {
      return a === b;
    }
    return a.iso2 === b.iso2;
  }

  onCountryChange(country: CountryPhone): void {
    this.selectedCountry.set(country);
    this.emitValue();
  }

  onNationalInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const value = target?.value ?? '';
    const cleaned = cleanPhoneInput(value);
    const parsed = tryParseFullNumber(cleaned, this.countries());
    if (parsed) {
      this.selectedCountry.set(parsed.country);
      this.nationalNumber.set(parsed.nationalNumber);
    } else {
      this.nationalNumber.set(cleaned);
    }
    this.emitValue();
  }

  emitValue(): void {
    const country = this.selectedCountry();
    if (!country) {
      this.onChange(null);
      return;
    }
    this.onChange(toE164(country, this.nationalNumber()));
  }

  writeValue(value: string | null): void {
    const list = this.countries();
    if (!list.length) {
      this.pendingE164Value = value;
      return;
    }
    const state = parseE164(value, list);
    const inList = list.find((c) => c.iso2 === state.country.iso2);
    if (inList) {
      this.selectedCountry.set(inList);
    } else if (value) {
      this.pendingCountryIso2 = state.country.iso2;
    } else {
      this.selectedCountry.set(state.country);
    }
    this.nationalNumber.set(state.nationalNumber);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  onBlur(): void {
    this.onTouched();
  }
}
